import { Button } from '@cian/ui-kit/button';
import { Checkbox } from '@cian/ui-kit/checkbox';
import { FormField } from '@cian/ui-kit/form';
import { Input } from '@cian/ui-kit/input';
import { Header2, InlineText2, InlineText3 } from '@cian/ui-kit/typography';

import { ChangeEvent, FC, MouseEvent, useCallback } from 'react';

import * as styles from './PreInfiniteSaveSearch.css';

export enum EStatus {
  Initial = 'initial',
  Loading = 'loading',
  Succeed = 'succeed',
  Failed = 'failed',
}

export interface IPreInfiniteSaveSearchProps {
  status: EStatus;
  emailConfirmationRequired: boolean | null;
  errors: string[] | null;
  isUserAuthenticated: boolean;
  email: string;
  subscribeToNews: boolean;
  onEmailChange(email: string): void;
  onSubscribeToNewsChange(subscribeToNews: boolean): void;
  onSubscribe(): void;
}

export const PreInfiniteSaveSearch: FC<IPreInfiniteSaveSearchProps> = ({
  status,
  emailConfirmationRequired,
  errors,
  isUserAuthenticated,
  email,
  subscribeToNews,
  onEmailChange,
  onSubscribeToNewsChange,
  onSubscribe,
}) => {
  const isLoading = status === EStatus.Loading;

  const handleEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, value: string) => {
      onEmailChange(value);
    },
    [onEmailChange],
  );

  const handleSubscribeToNewsChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, value: boolean) => {
      onSubscribeToNewsChange(value);
    },
    [onSubscribeToNewsChange],
  );

  const handleSubscribe = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      onSubscribe();
    },
    [onSubscribe],
  );

  return (
    <>
      <Header2 as="span" color="text-primary-default" fontWeight="bold">
        Сейчас это все объявления по вашему поиску.
        <br />
        Подпишитесь на обновления, и мы сообщим вам, если что-то появится
      </Header2>
      {status === EStatus.Succeed && (
        <div className={styles['message--succeed']}>
          <InlineText2>
            {emailConfirmationRequired ? (
              <span>
                На <b>{email}</b> отправлено письмо для активации подписки
              </span>
            ) : (
              'Мы оповестим вас, когда появятся новые объявления по вашему поиску'
            )}
          </InlineText2>
        </div>
      )}
      {status !== EStatus.Succeed && (
        <form className={styles['form']}>
          <FormField errorMessage={status === EStatus.Failed && errors ? errors.join('; ') : null}>
            <div className={styles['field--main']}>
              <Input
                disabled={isLoading}
                maxLength={254}
                placeholder="Ваш email"
                value={email}
                onChange={handleEmailChange}
              />
              <Button loading={isLoading} size="XS" theme="fill_primary" onClick={handleSubscribe}>
                Подписаться
              </Button>
            </div>
          </FormField>
          <FormField>
            <Checkbox
              checked={subscribeToNews}
              disabled={isLoading}
              label="Получать новости Cian.ru"
              onChange={handleSubscribeToNewsChange}
            />
          </FormField>
          {!isUserAuthenticated && (
            <div className={styles['license']}>
              <InlineText3 color="gray60_100">
                При подписке вы принимаете условия{' '}
                <a href="//www.cian.ru/help/about/rules/" rel="noreferrer" target="_blank">
                  Пользовательского соглашения
                </a>{' '}
                и{' '}
                <a href="//www.cian.ru/help/about/36526/" rel="noreferrer" target="_blank">
                  Политики конфиденциальности
                </a>
              </InlineText3>
            </div>
          )}
        </form>
      )}
    </>
  );
};
